import { useEffect, useRef, useState } from 'react';
import type { Directory } from '@witmetrics/utils';
import { useToggle } from '@/hooks/useToggle';
import { scrollIntoView } from '@/utils/dom';
import { CloseIcon, FileIcon, FolderIcon } from '@/icons';
import { generateEntryPreview } from '../../utils';
import { classes, getNodeSubtitle } from './utils';

export type EntryNodeProps = {
  contents: Directory | File;
  name: string;
  path: string[];
  onPreview?: (file: File) => void;
  onRemove?: (path: string[]) => void;
};

export default function EntryNode({
  contents,
  name,
  path,
  onPreview,
  onRemove,
}: EntryNodeProps) {
  const previewRef = useRef<string>();
  const activeRef = useRef(true);
  const childrenRef = useRef<HTMLDivElement>(null);
  const [previewSource, setPreviewSource] = useState<string>();
  const [isExpanded, toggleExpanded] = useToggle();

  const file = contents instanceof File ? contents : undefined;

  useEffect(() => {
    // Reset on (re)mount — StrictMode unmounts once, flipping this to false.
    activeRef.current = true;
    if (file) generatePreview(file);
    return () => {
      activeRef.current = false;
      if (previewRef.current) URL.revokeObjectURL(previewRef.current);
    };
  }, []);

  useEffect(() => {
    // Reveal the children when expanding a folder that sits below the fold.
    if (isExpanded) scrollIntoView(childrenRef.current, { block: 'nearest' });
  }, [isExpanded]);

  const generatePreview = async (file: File) => {
    try {
      const source = await generateEntryPreview(file);
      if (!source) return;
      // Bail if the row unmounted while we were rendering, and clean up.
      if (!activeRef.current) return URL.revokeObjectURL(source);
      previewRef.current = source;
      setPreviewSource(source);
    } catch {
      // An unreadable file just keeps its icon.
    }
  };

  const handleClick = () => {
    if (file) return onPreview?.(file);
    toggleExpanded();
  };

  const renderIcon = () => {
    if (previewSource) {
      return <img className={classes.img} src={previewSource} alt={name} />;
    }
    return (
      <div className={classes.iconSlot}>
        {file ? (
          <FileIcon className={classes.icon} />
        ) : (
          <FolderIcon className={classes.icon} />
        )}
      </div>
    );
  };

  const renderChildren = () => {
    if (contents instanceof File || !isExpanded) return null;
    return (
      <div ref={childrenRef} className={classes.children}>
        {Object.entries(contents).map(([childName, childContents]) => (
          <EntryNode
            key={childName}
            contents={childContents}
            name={childName}
            path={[...path, childName]}
            onPreview={onPreview}
            onRemove={onRemove}
          />
        ))}
      </div>
    );
  };

  return (
    <div className={classes.node}>
      <div className={classes.row}>
        <button
          className={classes.rowButton}
          type="button"
          onClick={handleClick}>
          {renderIcon()}
          <div className={classes.details}>
            <span className={classes.name}>{name}</span>
            <span className={classes.subtitle}>
              {getNodeSubtitle(contents)}
            </span>
          </div>
        </button>
        {onRemove && (
          <CloseIcon
            className={classes.removeIcon}
            onClick={() => onRemove(path)}
          />
        )}
      </div>
      {renderChildren()}
    </div>
  );
}
